import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  marketplaceScreenBibleApi,
  type AccessibilitySpec,
  type ComponentPlacement,
  type KeyboardShortcut,
  type ResponsiveRule,
  type ScreenBehaviour,
  type ScreenDeliverable,
  type ScreenInventoryEntry,
  type ScreenSpecValidation,
  type ScreenSpecification,
  type UXSDashboard,
  type UXSBibleStatus,
  type WorkspaceLayout,
} from '../../api/marketplace-screen-bible';

type Tab = 'overview' | 'inventory' | 'specs' | 'layouts' | 'placement' | 'behaviour' | 'keyboard' | 'responsive' | 'status';
type Theme = 'light' | 'dark';

const priorityColors: Record<string, string> = {
  p0: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
  p1: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  p2: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
};

export function MarketplaceScreenBiblePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<UXSDashboard | null>(null);
  const [inventory, setInventory] = useState<ScreenInventoryEntry[]>([]);
  const [specs, setSpecs] = useState<ScreenSpecification[]>([]);
  const [dashboardLayout, setDashboardLayout] = useState<WorkspaceLayout | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceLayout[]>([]);
  const [placements, setPlacements] = useState<ComponentPlacement[]>([]);
  const [behaviours, setBehaviours] = useState<ScreenBehaviour[]>([]);
  const [shortcuts, setShortcuts] = useState<KeyboardShortcut[]>([]);
  const [responsive, setResponsive] = useState<ResponsiveRule[]>([]);
  const [accessibility, setAccessibility] = useState<AccessibilitySpec[]>([]);
  const [deliverables, setDeliverables] = useState<ScreenDeliverable[]>([]);
  const [status, setStatus] = useState<UXSBibleStatus | null>(null);
  const [screenResult, setScreenResult] = useState<ScreenSpecValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, inv, sp, dl, ws, pl, bh, sc, rs, acc, del, st] = await Promise.all([
        marketplaceScreenBibleApi.dashboard(),
        marketplaceScreenBibleApi.screenInventory(),
        marketplaceScreenBibleApi.screenSpecifications(),
        marketplaceScreenBibleApi.dashboardLayout(),
        marketplaceScreenBibleApi.workspaceLayouts(),
        marketplaceScreenBibleApi.componentPlacements(),
        marketplaceScreenBibleApi.screenBehaviours(),
        marketplaceScreenBibleApi.keyboardShortcuts(),
        marketplaceScreenBibleApi.responsiveRules(),
        marketplaceScreenBibleApi.accessibility(),
        marketplaceScreenBibleApi.deliverables(),
        marketplaceScreenBibleApi.status(),
      ]);
      setDashboard(dash);
      setInventory(inv);
      setSpecs(sp);
      setDashboardLayout(dl);
      setWorkspaces(ws);
      setPlacements(pl);
      setBehaviours(bh);
      setShortcuts(sc);
      setResponsive(rs);
      setAccessibility(acc);
      setDeliverables(del);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load marketplace screen bible');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validateScreen = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await marketplaceScreenBibleApi.validateScreen({
        screen_key: 'dashboard',
        attrs: {
          purpose_defined: true, layout_specified: true, components_placed: true,
          interactions_documented: true, keyboard_shortcuts: true, loading_behaviour: true,
          empty_state: true, error_state: true, permissions: true, performance_target: true,
          ai_behaviour: true, navigation_rules: true, success_criteria: true,
        },
      });
      setScreenResult(result);
      setActionMsg(result.valid ? 'Screen passes UXS-000001 specification gate' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-teal-900/40 bg-slate-900' : 'border-teal-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-teal-50/20 text-slate-900';
  const accent = dark ? 'text-teal-300' : 'text-teal-700';
  const tabActive = 'bg-teal-600 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Executive Summary' },
    { id: 'inventory', label: 'Screen Inventory' },
    { id: 'specs', label: 'Screen Specs' },
    { id: 'layouts', label: 'Workspace Layouts' },
    { id: 'placement', label: 'Component Placement' },
    { id: 'behaviour', label: 'Screen Behaviour' },
    { id: 'keyboard', label: 'Keyboard & A11y' },
    { id: 'responsive', label: 'Responsive & Deliverables' },
    { id: 'status', label: 'Bible Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>UXS-000001</p>
          <h1 className="text-2xl font-semibold">Marketplace Screen Bible</h1>
          <p className={`text-sm ${muted}`}>
            Complete screen inventory, wireframes, layout specifications, user flows, component placement and screen-level engineering blueprint
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/marketplace-mos-bible" className={`${accent} hover:underline`}>MIB-000005 MOS</Link>
            <span className={muted}>→</span>
            <Link to="/platform/enterprise-design-system" className={`${accent} hover:underline`}>EDS-000001</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Screen-Level Engineering Blueprint</h2>
            <p className={`mt-1 text-sm ${muted}`}>Depends on: {dashboard.depends_on.join(' · ')}</p>
            <p className={`mt-3 text-sm ${muted}`}>
              Every marketplace screen is fully specified before React development begins.
              No frontend engineer shall need to make UX decisions during implementation.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Screens', value: dashboard.screen_inventory },
              { label: 'Specifications', value: dashboard.screen_specifications },
              { label: 'Workspace Layouts', value: dashboard.workspace_layouts },
              { label: 'Component Placements', value: dashboard.component_placements },
              { label: 'Screen Behaviours', value: dashboard.screen_behaviours },
              { label: 'Keyboard Shortcuts', value: dashboard.keyboard_shortcuts },
              { label: 'Responsive Rules', value: dashboard.responsive_rules },
              { label: 'Deliverables', value: dashboard.deliverables },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'inventory' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead>
              <tr className={`border-b text-left ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
                <th className="px-4 py-2">Screen</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Route</th>
                <th className="px-4 py-2">Priority</th>
                <th className="px-4 py-2">MVP</th>
              </tr>
            </thead>
            <tbody>
              {inventory.map((s) => (
                <tr key={s.key} className={`border-b ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2 font-medium">{s.name}</td>
                  <td className={`px-4 py-2 ${muted}`}>{s.category}</td>
                  <td className={`px-4 py-2 font-mono text-xs ${muted}`}>{s.route ?? '—'}</td>
                  <td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs ${priorityColors[s.priority] ?? ''}`}>{s.priority.toUpperCase()}</span></td>
                  <td className="px-4 py-2">{s.mvp ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'specs' && (
        <div className="space-y-4">
          {specs.map((s) => (
            <div key={s.key} className={`rounded-xl border p-5 ${card}`}>
              <h3 className="text-lg font-semibold">{s.name}</h3>
              <p className={`mt-2 text-sm ${muted}`}><strong>Purpose:</strong> {s.purpose}</p>
              <p className={`mt-1 text-sm ${muted}`}><strong>Layout:</strong> {s.layout}</p>
              <p className={`mt-1 text-sm ${muted}`}><strong>Performance:</strong> {s.performance_target}</p>
              <div className="mt-3 flex flex-wrap gap-1">
                {s.components.map((c) => <span key={c} className="rounded bg-teal-100 px-2 py-0.5 text-xs dark:bg-teal-900">{c}</span>)}
              </div>
              <ul className={`mt-3 list-inside list-disc text-sm ${muted}`}>
                {s.success_criteria.map((c) => <li key={c}>{c}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}

      {tab === 'layouts' && (
        <div className="space-y-6">
          {dashboardLayout && (
            <section>
              <h2 className="mb-3 text-lg font-semibold">Dashboard Layout ({dashboardLayout.regions.length} regions)</h2>
              <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                {dashboardLayout.regions.map((r) => (
                  <div key={r.key} className={`rounded-lg border p-3 ${card}`}>
                    <span className="text-xs text-teal-600 dark:text-teal-400">{r.order}</span>
                    <h3 className="font-semibold">{r.name}</h3>
                    {r.component && <code className={`text-xs ${muted}`}>{r.component}</code>}
                  </div>
                ))}
              </div>
            </section>
          )}
          <section>
            <h2 className="mb-3 text-lg font-semibold">Workspace Layouts</h2>
            {workspaces.filter((w) => w.key !== 'dashboard').map((w) => (
              <div key={w.key} className={`mb-4 rounded-xl border p-4 ${card}`}>
                <h3 className="font-semibold">{w.name}</h3>
                {w.tabs && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {w.tabs.map((t) => <span key={t} className="rounded bg-cyan-100 px-2 py-0.5 text-xs dark:bg-cyan-900">{t}</span>)}
                  </div>
                )}
                <p className={`mt-2 text-xs ${muted}`}>{w.regions.length} regions · {w.actions?.length ?? 0} actions</p>
              </div>
            ))}
          </section>
        </div>
      )}

      {tab === 'placement' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead>
              <tr className={`border-b text-left ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
                <th className="px-4 py-2">Component</th>
                <th className="px-4 py-2">Screen</th>
                <th className="px-4 py-2">Region</th>
                <th className="px-4 py-2">Position</th>
              </tr>
            </thead>
            <tbody>
              {placements.map((p) => (
                <tr key={p.key} className={`border-b ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2 font-medium">{p.component}</td>
                  <td className={`px-4 py-2 ${muted}`}>{p.screen}</td>
                  <td className={`px-4 py-2 ${muted}`}>{p.region}</td>
                  <td className={`px-4 py-2 text-xs ${muted}`}>{p.position}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'behaviour' && (
        <div className="grid gap-3 md:grid-cols-2">
          {behaviours.map((b) => (
            <div key={b.key} className={`rounded-xl border p-4 ${card}`}>
              <span className="rounded bg-teal-100 px-2 py-0.5 text-xs dark:bg-teal-900">{b.category}</span>
              <h3 className="mt-2 font-semibold">{b.name}</h3>
              <p className={`text-sm ${muted}`}>{b.description}</p>
              {b.target && <p className={`mt-1 text-xs ${muted}`}>Target: {b.target}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'keyboard' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Keyboard Shortcuts</h2>
            <div className="space-y-2">
              {shortcuts.map((s) => (
                <div key={s.key} className={`flex items-center gap-4 rounded-lg border px-4 py-2 ${card}`}>
                  <code className="shrink-0 rounded bg-slate-100 px-2 py-1 text-sm dark:bg-slate-800">{s.shortcut}</code>
                  <span className="font-medium">{s.action}</span>
                  <span className={`ml-auto text-xs ${muted}`}>{s.scope}</span>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Accessibility</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {accessibility.map((a) => (
                <div key={a.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{a.name}</h3>
                  <p className={`text-sm ${muted}`}>{a.requirement}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'responsive' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Responsive Behaviour</h2>
            <div className="space-y-3">
              {responsive.map((r) => (
                <div key={r.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{r.breakpoint}</h3>
                  <p className={`text-sm ${muted}`}>{r.layout}</p>
                  {r.description && <p className={`mt-1 text-xs ${muted}`}>{r.description}</p>}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Deliverables</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {deliverables.map((d) => (
                <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{d.name}</h3>
                    <span className={`rounded px-2 py-0.5 text-xs ${d.status === 'complete' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200' : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'}`}>
                      {d.status}
                    </span>
                  </div>
                  {d.description && <p className={`mt-1 text-sm ${muted}`}>{d.description}</p>}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'status' && status && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">{status.status}</span>
            {status.no_ux_decisions_needed && <p className={`mt-3 text-sm ${muted}`}>No UX decisions needed during frontend implementation</p>}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Validate Screen Specification</h3>
            <button type="button" disabled={busy} onClick={validateScreen} className={`mt-4 rounded-lg px-4 py-2 text-sm text-white ${tabActive} disabled:opacity-50`}>
              {busy ? 'Validating…' : 'Run Sample Validation'}
            </button>
            {screenResult && (
              <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-100 p-3 text-xs dark:bg-slate-800">{JSON.stringify(screenResult, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
